import { cn } from '@c2fo/liquidity';
import ClosedMarketChip from '@/enterprise/components/ClosedMarketChip';
import useAcceptanceCardDetails from './useAcceptanceCardDetails';

export const ACCEPTANCE_CARD_TYPES = [
  'included',
  'likelyToBeAccepted',
  'unlikelyToBeAccepted',
  'nothingOffered',
] as const;
export type AcceptanceCardType = (typeof ACCEPTANCE_CARD_TYPES)[number];

const acceptanceCardConfig = {
  included: {
    borderColor: 'border-white',
  },
  likelyToBeAccepted: {
    borderColor: 'border-green-500',
  },
  unlikelyToBeAccepted: {
    borderColor: 'border-red-500',
  },
  nothingOffered: {
    borderColor: 'border-yellow-500',
  },
} satisfies Record<AcceptanceCardType, { borderColor: string }>;

const AcceptanceCard = ({
  cardType,
  listeningForMarketStats,
}: {
  cardType: AcceptanceCardType;
  listeningForMarketStats: boolean;
}) => {
  const acceptanceCardDetails = useAcceptanceCardDetails(cardType);

  if (!acceptanceCardDetails) {
    return null;
  }

  const {
    acceptanceText,
    discountAmountSelectedCurrency,
    totalInvoiceAmountSelectedCurrency,
    numberOfDivisionsText,
    daysPaidEarlyText,
    numberOfInvoicesText,
  } = acceptanceCardDetails;

  const hasClosedMarkets = acceptanceCardDetails.hasClosedMarkets ?? false;

  return (
    <div
      className={cn(
        'flex flex-col justify-between rounded-md border-l-4 bg-secondary-500 p-3 text-white shadow-md lg:flex-row lg:items-center lg:gap-6',
        acceptanceCardConfig[cardType].borderColor,
        { 'border-l-0 bg-transparent px-2 pb-1 pt-0 shadow-none': cardType === 'included' }
      )}
    >
      <div>
        <div className={cn('font-medium', { 'font-bold': cardType === 'included' })}>{acceptanceText}</div>
        {discountAmountSelectedCurrency && (
          <div className={cn('text-sm', { 'blur-sm': listeningForMarketStats })}>{discountAmountSelectedCurrency}</div>
        )}
        {numberOfDivisionsText && daysPaidEarlyText && numberOfInvoicesText && (
          <div className={cn('text-sm', { 'blur-sm': listeningForMarketStats })}>
            {`${numberOfDivisionsText} | ${daysPaidEarlyText} | ${numberOfInvoicesText}`}
          </div>
        )}
        {hasClosedMarkets && (
          <div className={cn('mt-1', { 'blur-sm': listeningForMarketStats })}>
            <ClosedMarketChip
              count={acceptanceCardDetails?.closedMarketCount}
              invoiceAmount={acceptanceCardDetails?.closedInvoiceAmount}
            />
          </div>
        )}
      </div>
      <div
        className={cn(
          'text-xl font-medium',
          { 'blur-sm': listeningForMarketStats },
          { 'text-2xl font-bold': cardType === 'included' }
        )}
      >
        {totalInvoiceAmountSelectedCurrency}
      </div>
    </div>
  );
};

export default AcceptanceCard;
